import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const BreadcrumbContext = createContext();

const getBreadcrumbLabel = (item) =>
  item?.name ?? item?.tagNumber ?? item?.title ?? item?.displayName ?? null;

const isSameBreadcrumbItem = (currentItem, nextItem) => {
  if (currentItem === nextItem) return true;
  if (!currentItem || !nextItem) return false;

  return (
    String(currentItem.id ?? "") === String(nextItem.id ?? "") &&
    getBreadcrumbLabel(currentItem) === getBreadcrumbLabel(nextItem)
  );
};

export const BreadcrumbProvider = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState(() => {
    const saved = localStorage.getItem("breadcrumb-state");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error("Error parsing saved breadcrumbs:", error);
      }
    }
    return {
      site: null,
      plant: null,
      asset: null,
      subAsset: null,
      component: null,
      instrument: null,
      project: null,
    };
  });

  // Add selected items state
  const [selectedItems, setSelectedItems] = useState({
    site: null,
    plant: null,
    asset: null,
    subAsset: null,
    component: null,
    instrument: null,
    project: null,
  });

  // Add this function to your BreadcrumbContext, before the return statement
  const setSelectedItem = useCallback((level, item) => {
    setSelectedItems((prev) =>
      isSameBreadcrumbItem(prev[level], item)
        ? prev
        : { ...prev, [level]: item },
    );
  }, []);

  // Save to localStorage whenever breadcrumbs change
  useEffect(() => {
    localStorage.setItem("breadcrumb-state", JSON.stringify(breadcrumbs));
  }, [breadcrumbs]);

  // Store full objects with both display name and ID/data
  const setSite = useCallback((siteData) => {
    setBreadcrumbs((prev) => {
      const sameSite = isSameBreadcrumbItem(prev.site, siteData);
      const shouldClearChildren =
        prev.plant !== null ||
        prev.asset !== null ||
        prev.subAsset !== null ||
        prev.component !== null ||
        prev.instrument !== null;

      if (sameSite && !shouldClearChildren) return prev;

      return {
        ...prev,
        site: siteData,
        // Clear downstream breadcrumbs when setting a new site
        plant: null,
        asset: null,
        subAsset: null,
        component: null,
        instrument: null,
      };
    });
    setSelectedItems((prev) =>
      isSameBreadcrumbItem(prev.site, siteData)
        ? prev
        : { ...prev, site: siteData },
    );
  }, []);

  const setPlant = useCallback((plantData) => {
    setBreadcrumbs((prev) => {
      const samePlant = isSameBreadcrumbItem(prev.plant, plantData);
      const shouldClearChildren =
        prev.asset !== null ||
        prev.subAsset !== null ||
        prev.component !== null ||
        prev.instrument !== null;

      if (samePlant && !shouldClearChildren) return prev;

      return {
        ...prev,
        plant: plantData,
        // Clear downstream breadcrumbs when setting a new plant
        asset: null,
        subAsset: null,
        component: null,
        instrument: null,
      };
    });
    setSelectedItems((prev) =>
      isSameBreadcrumbItem(prev.plant, plantData)
        ? prev
        : { ...prev, plant: plantData },
    );
  }, []);

  const setAsset = useCallback((assetData) => {
    setBreadcrumbs((prev) => {
      const sameAsset = isSameBreadcrumbItem(prev.asset, assetData);
      const shouldClearChildren =
        prev.subAsset !== null ||
        prev.component !== null ||
        prev.instrument !== null;

      if (sameAsset && !shouldClearChildren) return prev;

      return {
        ...prev,
        asset: assetData,
        // Clear downstream breadcrumbs when setting a new asset
        subAsset: null,
        component: null,
        instrument: null,
      };
    });
    setSelectedItems((prev) =>
      isSameBreadcrumbItem(prev.asset, assetData)
        ? prev
        : { ...prev, asset: assetData },
    );
  }, []);

  const setSubAsset = useCallback((subAssetData) => {
    setBreadcrumbs((prev) => {
      const sameSubAsset = isSameBreadcrumbItem(prev.subAsset, subAssetData);
      const shouldClearComponent =
        prev.component !== null || prev.instrument !== null;

      if (sameSubAsset && !shouldClearComponent) return prev;

      return {
        ...prev,
        subAsset: subAssetData,
        component: null,
        instrument: null,
      };
    });
    setSelectedItems((prev) =>
      isSameBreadcrumbItem(prev.subAsset, subAssetData)
        ? prev
        : { ...prev, subAsset: subAssetData },
    );
  }, []);

  const setComponent = useCallback((componentData) => {
    setBreadcrumbs((prev) => {
      const sameComponent = isSameBreadcrumbItem(
        prev.component,
        componentData,
      );
      const shouldClearInstrument = prev.instrument !== null;

      if (sameComponent && !shouldClearInstrument) return prev;

      return { ...prev, component: componentData, instrument: null };
    });
    setSelectedItems((prev) =>
      isSameBreadcrumbItem(prev.component, componentData)
        ? prev
        : { ...prev, component: componentData, instrument: null },
    );
  }, []);

  const setInstrument = useCallback((instrumentData) => {
    setBreadcrumbs((prev) =>
      isSameBreadcrumbItem(prev.instrument, instrumentData)
        ? prev
        : { ...prev, instrument: instrumentData },
    );
    setSelectedItems((prev) =>
      isSameBreadcrumbItem(prev.instrument, instrumentData)
        ? prev
        : { ...prev, instrument: instrumentData },
    );
  }, []);

  const setProject = useCallback((projectData) => {
    setBreadcrumbs((prev) =>
      isSameBreadcrumbItem(prev.project, projectData)
        ? prev
        : { ...prev, project: projectData },
    );
    setSelectedItems((prev) =>
      isSameBreadcrumbItem(prev.project, projectData)
        ? prev
        : { ...prev, project: projectData },
    );
  }, []);

  // Function to get selected item
  const getSelectedItem = useCallback(
    (level) => {
      return selectedItems[level];
    },
    [selectedItems],
  );

  // Clear specific breadcrumbs (useful for navigation)
  const clearFromLevel = useCallback((level) => {
    setBreadcrumbs((prev) => {
      const newState = { ...prev };
      switch (level) {
        case "site":
          newState.site = null;
          newState.plant = null;
          newState.asset = null;
          newState.subAsset = null;
          newState.component = null;
          newState.instrument = null;
          setSelectedItems({
            site: null,
            plant: null,
            asset: null,
            subAsset: null,
            component: null,
            instrument: null,
          });
          break;
        case "plant":
          newState.plant = null;
          newState.asset = null;
          newState.subAsset = null;
          newState.component = null;
          newState.instrument = null;
          setSelectedItems((prev) => ({
            ...prev,
            plant: null,
            asset: null,
            subAsset: null,
            component: null,
            instrument: null,
          }));
          break;
        case "asset":
          newState.asset = null;
          newState.subAsset = null;
          newState.component = null;
          newState.instrument = null;
          setSelectedItems((prev) => ({
            ...prev,
            asset: null,
            subAsset: null,
            component: null,
            instrument: null,
          }));
          break;
        case "subAsset":
          newState.subAsset = null;
          newState.component = null;
          newState.instrument = null;
          setSelectedItems((prev) => ({
            ...prev,
            subAsset: null,
            component: null,
            instrument: null,
          }));
          break;
        case "component":
          newState.component = null;
          newState.instrument = null;
          setSelectedItems((prev) => ({
            ...prev,
            component: null,
            instrument: null,
          }));
          break;
        case "instrument":
          newState.instrument = null;
          setSelectedItems((prev) => ({ ...prev, instrument: null }));
          break;
        case "project":
          newState.project = null;
          break;
        default:
          break;
      }
      return newState;
    });
  }, []);

  // Clear all breadcrumbs
  const clearAllBreadcrumbs = useCallback(() => {
    setBreadcrumbs({
      site: null,
      plant: null,
      asset: null,
      subAsset: null,
      component: null,
      instrument: null,
      project: null,
    });
  }, []);

  // Helper function to get breadcrumb display name
  const getDisplayName = useCallback(
    (level) => {
      const item = breadcrumbs[level];
      if (!item) return null;

      // Try common property names for display name
      return (
        item.name || item.tagNumber || item.title || item.displayName || item.id
      );
    },
    [breadcrumbs],
  );

  const contextValue = useMemo(
    () => ({
      breadcrumbs,
      selectedItems,
      setSite,
      setPlant,
      setAsset,
      setSubAsset,
      setComponent,
      setInstrument,
      setProject,
      setSelectedItem,
      getSelectedItem,
      clearFromLevel,
      clearAllBreadcrumbs,
      getDisplayName,
    }),
    [
      breadcrumbs,
      selectedItems,
      setSite,
      setPlant,
      setAsset,
      setSubAsset,
      setComponent,
      setInstrument,
      setProject,
      setSelectedItem,
      getSelectedItem,
      clearFromLevel,
      clearAllBreadcrumbs,
      getDisplayName,
    ],
  );

  return (
    <BreadcrumbContext.Provider value={contextValue}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumbs = () => useContext(BreadcrumbContext);
